import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useField } from 'formik';
import { SelectProps } from 'antd';
import cn from 'classnames';

import { Skill } from 'shared/generated-models';
import { FormikSelect, Icon, Text } from 'shared/components/ui';
import { useDebounce, useOnClickOutside } from 'shared/hooks';

import styles from './index.module.scss';
import './add-tag.scss';

interface Props extends SelectProps {
	className?: string;
	useQuery: any;
	name: string;
	label?: ReactNode;
	similar?: string[];
	type?: 'github' | 'stackoverflow' | 'kaggle';
}

export const SearchSelect: FC<Props> = ({ className, useQuery, name, label, similar, type, ...props }: Props) => {
	const [field, , helper] = useField(name);
	const value = (field.value as []) || [];
	const dropdownRef = useRef<HTMLDivElement | null>(null);

	const handleClear = () => {
		helper.setValue([], true);
	};

	const handleSimilar = (selected: string) => {
		helper.setValue([...value, selected]);
	};

	const [options, setOptions] = useState([]);

	useEffect(() => {
		dropdownRef.current?.children[0]?.children[0]?.scroll({ top: 0 });
	}, [options]);

	const isSkills = name === 'required_skills' || name === 'bonus_skills';

	const [searchQuery, setSearchQuery] = useState('');
	const debouncedSearchQuery = useDebounce(
		isSkills
			? searchQuery.replace(/\s+/g, ' ').replaceAll(' ', '-').trim()
			: searchQuery.replace(/\s+/g, ' ').trim(),
		300
	);

	const handleSearch = (search: string) => {
		setSearchQuery(search);
	};

	const ref = useRef(null);
	const [isFocused, setIsFocused] = useState(false);

	const handleFocusIn = () => {
		setIsFocused(true);
	};

	const handleFocusOut = () => {
		setIsFocused(false);
	};

	useOnClickOutside(ref, handleFocusOut);

	const { data } = useQuery(debouncedSearchQuery);

	useEffect(() => {
		setOptions(
			data?.map((item: Skill) => {
				return {
					label: item.name,
					value: item.name
				};
			}) || []
		);
	}, [data]);

	return (
		<div className={styles.wrapper}>
			<FormikSelect
				ref={ref}
				name={name}
				size="middle"
				label={
					<div className={styles.label} onClick={e => e.preventDefault()}>
						<Text variant="inter/15/medium">{label}</Text>
						{value.length > 0 && (
							<div
								className={cn(styles.labelTag, {
									[styles.labelTagGithub]: type === 'github',
									[styles.labelTagStackoverflow]: type === 'stackoverflow'
								})}>
								<Text variant="inter/14/medium">{value.length}</Text>
								<button type="button" onClick={handleClear}>
									<Icon icon="close" />
								</button>
							</div>
						)}
					</div>
				}
				onFocus={handleFocusIn}
				onBlur={handleFocusOut}
				dropdownRender={dropdown => <div ref={dropdownRef}>{dropdown}</div>}
				open={isFocused}
				value={value}
				type={type}
				menuItemSelectedIcon={null}
				onSearch={handleSearch}
				className={cn(
					styles.select,
					className,
					{ 'SearchSelect_add-tag': !isFocused && value.length !== 0 },
					{ 'SearchSelect_add-tag_github': type === 'github' },
					{ 'SearchSelect_add-tag_stackoverflow': type === 'stackoverflow' }
				)}
				mode="multiple"
				notFoundContent={null}
				filterOption={false}
				options={options}
				help={
					similar &&
					value.length > 0 && (
						<div className={styles.similar}>
							{similar.slice(0, 6).map((item, idx) => (
								<button key={idx} className={styles.similarSkill} onClick={() => handleSimilar(item)}>
									<Icon className={styles.similarSkillIcon} icon="plus" />
									<Text variant="inter/14/medium">{item}</Text>
								</button>
							))}
						</div>
					)
				}
				{...props}
			/>
		</div>
	);
};
